import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { agencyService } from '../../services/agencyService';
import { toast } from 'react-toastify';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const AgencySchema = Yup.object().shape({
  name: Yup.string().required('Acente adı gereklidir'),
  email: Yup.string().email('Geçerli bir e-posta adresi giriniz').required('E-posta adresi gereklidir'),
  phone: Yup.string().required('Telefon numarası gereklidir'),
  contactPerson: Yup.string().required('İletişim kişisi gereklidir'),
  address: Yup.string(),
  taxNumber: Yup.string(),
  taxOffice: Yup.string(),
  website: Yup.string().url('Geçerli bir URL giriniz').nullable(),
  isActive: Yup.boolean()
});

const AgencyCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    try {
      await agencyService.createAgency(values);
      toast.success('Acente başarıyla oluşturuldu');
      navigate('/agencies');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Acente oluşturulurken bir hata oluştu');
      setSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Yeni Acente Ekle</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/agencies"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
            Geri Dön
          </Link>
        </div>
      </div>

      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Acente Bilgileri</h3>
            <p className="mt-1 text-sm text-gray-500">
              Yeni bir acente eklemek için gerekli bilgileri doldurun.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <Formik
              initialValues={{
                name: '',
                email: '',
                phone: '',
                contactPerson: '',
                address: '',
                taxNumber: '',
                taxOffice: '',
                website: '',
                isActive: true,
                notes: ''
              }}
              validationSchema={AgencySchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <div className="space-y-6">
                    {/* Acente Adı */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="form-label">
                          Acente Adı *
                        </label>
                        <Field
                          type="text"
                          name="name"
                          id="name"
                          className={`form-input ${
                            errors.name && touched.name ? 'form-input-error' : ''
                          }`}
                        />
                        <ErrorMessage name="name" component="div" className="form-error" />
                      </div>

                      <div>
                        <label htmlFor="contactPerson" className="form-label">
                          İletişim Kişisi *
                        </label>
                        <Field
                          type="text"
                          name="contactPerson"
                          id="contactPerson"
                          className={`form-input ${
                            errors.contactPerson && touched.contactPerson ? 'form-input-error' : ''
                          }`}
                        />
                        <ErrorMessage name="contactPerson" component="div" className="form-error" />
                      </div>
                    </div>

                    {/* İletişim Bilgileri */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="form-label">
                          E-posta Adresi *
                        </label>
                        <Field
                          type="email"
                          name="email"
                          id="email"
                          className={`form-input ${
                            errors.email && touched.email ? 'form-input-error' : ''
                          }`}
                        />
                        <ErrorMessage name="email" component="div" className="form-error" />
                      </div>

                      <div>
                        <label htmlFor="phone" className="form-label">
                          Telefon Numarası *
                        </label>
                        <Field
                          type="text"
                          name="phone"
                          id="phone"
                          className={`form-input ${
                            errors.phone && touched.phone ? 'form-input-error' : ''
                          }`}
                        />
                        <ErrorMessage name="phone" component="div" className="form-error" />
                      </div>
                    </div>

                    {/* Adres */}
                    <div>
                      <label htmlFor="address" className="form-label">
                        Adres
                      </label>
                      <Field
                        as="textarea"
                        name="address"
                        id="address"
                        rows={3}
                        className="form-textarea"
                      />
                    </div>

                    {/* Vergi Bilgileri */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="taxNumber" className="form-label">
                          Vergi Numarası
                        </label>
                        <Field
                          type="text"
                          name="taxNumber"
                          id="taxNumber"
                          className="form-input"
                        />
                      </div>

                      <div>
                        <label htmlFor="taxOffice" className="form-label">
                          Vergi Dairesi
                        </label>
                        <Field
                          type="text"
                          name="taxOffice"
                          id="taxOffice"
                          className="form-input"
                        />
                      </div>
                    </div>

                    {/* Web Sitesi */}
                    <div>
                      <label htmlFor="website" className="form-label">
                        Web Sitesi
                      </label>
                      <Field
                        type="text"
                        name="website"
                        id="website"
                        className={`form-input ${
                          errors.website && touched.website ? 'form-input-error' : ''
                        }`}
                        placeholder="https://www.example.com"
                      />
                      <ErrorMessage name="website" component="div" className="form-error" />
                    </div>

                    {/* Aktif */}
                    <div>
                      <div className="flex items-center h-5 mt-4">
                        <Field
                          type="checkbox"
                          name="isActive"
                          id="isActive"
                          className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-3 block text-sm font-medium text-gray-700">
                          Aktif
                        </label>
                      </div>
                    </div>

                    {/* Notlar */}
                    <div>
                      <label htmlFor="notes" className="form-label">
                        Notlar
                      </label>
                      <Field
                        as="textarea"
                        name="notes"
                        id="notes"
                        rows={3}
                        className="form-textarea"
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end space-x-3">
                    <Link
                      to="/agencies"
                      className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                    >
                      İptal
                    </Link>
                    <button
                      type="submit"
                      disabled={isSubmitting || isLoading}
                      className="px-4 py-2 bg-primary-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading || isSubmitting ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Kaydediliyor...</span>
                        </div>
                      ) : (
                        'Kaydet'
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyCreate;
